import { useState, useRef, useEffect, useCallback } from 'react'
import { MapView } from './MapView'
import { CompactTariffCard } from './TariffCard'
import { ServiceTabs, getCategoryByTariffClass, type ServiceCategory } from './ServiceTabs'
import { EntranceModal } from './BottomSheet'
import type { AddressResult, ServiceLevel } from '../types'

// USDT иконка
const UsdtIcon = () => (
  <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="14" fill="#26a17b"/>
    <path d="M17.5 17.5V22H14.5V17.5H9V15H14.5V13.5C11.5 13.2 9.5 12.5 9.5 11.5C9.5 10.3 12.5 9.5 16 9.5C19.5 9.5 22.5 10.3 22.5 11.5C22.5 12.5 20.5 13.2 17.5 13.5V15H23V17.5H17.5Z" fill="white"/>
  </svg>
)

// Иконка настроек
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="21" x2="4" y2="14"/>
    <line x1="4" y1="10" x2="4" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12" y2="3"/>
    <line x1="20" y1="21" x2="20" y2="16"/>
    <line x1="20" y1="12" x2="20" y2="3"/>
    <line x1="1" y1="14" x2="7" y2="14"/>
    <line x1="9" y1="8" x2="15" y2="8"/>
    <line x1="17" y1="16" x2="23" y2="16"/>
  </svg>
)

// Иконка удаления
const RemoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)

interface MainScreenProps {
  fromAddress: AddressResult | null
  toAddress: AddressResult | null
  intermediateAddresses: AddressResult[]
  tariffs: ServiceLevel[]
  selectedTariff: string
  isOrdering: boolean
  isLoadingTariffs?: boolean
  tariffsError?: string | null
  onFromClick: () => void
  onToClick: () => void
  onAddIntermediateClick: () => void
  onRemoveIntermediate: (index: number) => void
  onTariffSelect: (tariff: string) => void
  onOrder: () => void
  onTariffDetails?: (tariff: string) => void
  // Данные подъезда
  entrance: string
  onEntranceChange: (value: string) => void
  onHistoryClick?: () => void
}

export const MainScreen = ({
  fromAddress,
  toAddress,
  intermediateAddresses,
  tariffs,
  selectedTariff,
  isOrdering,
  isLoadingTariffs = false,
  tariffsError = null,
  onFromClick,
  onToClick,
  onAddIntermediateClick,
  onRemoveIntermediate,
  onTariffSelect,
  onOrder,
  onTariffDetails,
  entrance,
  onEntranceChange,
  onHistoryClick,
}: MainScreenProps) => {
  const [serviceCategory, setServiceCategory] = useState<ServiceCategory>('taxi')
  const [showEntranceModal, setShowEntranceModal] = useState(false)
  const tariffScrollRef = useRef<HTMLDivElement>(null)
  
  const selectedTariffData = tariffs.find(t => t.class === selectedTariff)
  const canOrder = fromAddress && toAddress && selectedTariff && tariffs.length > 0 && !isLoadingTariffs && !tariffsError
  
  // Время прибытия
  const getArrivalTime = () => {
    if (!selectedTariffData?.estimated_waiting) return null
    const now = new Date()
    const arrivalMinutes = Math.ceil(selectedTariffData.estimated_waiting.seconds / 60)
    now.setMinutes(now.getMinutes() + arrivalMinutes)
    return now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }

  // Форматирование данных подъезда
  const getEntranceInfo = () => {
    return entrance ? `подъезд ${entrance}` : null
  }

  // Автопереключение категории при скролле
  const handleTariffScroll = useCallback(() => {
    if (!tariffScrollRef.current) return
    
    const container = tariffScrollRef.current
    const scrollLeft = container.scrollLeft
    const containerWidth = container.offsetWidth
    
    // Определяем центр видимой области
    const centerPosition = scrollLeft + containerWidth / 2
    
    // Находим карточку в центре
    const cards = container.querySelectorAll('[data-tariff-class]')
    let closestCard: Element | null = null
    let minDistance = Infinity
    
    cards.forEach((card) => {
      const rect = card.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const cardCenter = rect.left - containerRect.left + rect.width / 2 + scrollLeft
      const distance = Math.abs(cardCenter - centerPosition)
      
      if (distance < minDistance) {
        minDistance = distance
        closestCard = card
      }
    })
    
    if (closestCard) {
      const tariffClass = (closestCard as Element).getAttribute('data-tariff-class')
      if (tariffClass) {
        const newCategory = getCategoryByTariffClass(tariffClass)
        if (newCategory !== serviceCategory) {
          setServiceCategory(newCategory)
        }
      }
    }
  }, [serviceCategory])

  // Слушатель скролла
  useEffect(() => {
    const container = tariffScrollRef.current
    if (!container) return
    
    container.addEventListener('scroll', handleTariffScroll)
    return () => container.removeEventListener('scroll', handleTariffScroll)
  }, [handleTariffScroll])

  // При смене категории скроллим к первому тарифу этой категории
  const handleCategoryChange = (category: ServiceCategory) => {
    setServiceCategory(category)
    
    if (!tariffScrollRef.current) return
    
    const container = tariffScrollRef.current
    const firstCard = container.querySelector(`[data-category="${category}"]`)
    
    if (firstCard) {
      const containerRect = container.getBoundingClientRect()
      const cardRect = firstCard.getBoundingClientRect()
      const scrollLeft = cardRect.left - containerRect.left + container.scrollLeft - 16
      
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-dark-tertiary/95 backdrop-blur-sm px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between border-b border-primary/10 relative z-10">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5S5.67 13 6.5 13 8 13.67 8 14.5 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5S16.67 13 17.5 13 19 13.67 19 14.5 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm sm:text-base">Prime Taxi</h1>
            <p className="text-text-muted text-[10px] sm:text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-green animate-pulse" />
              24/7
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* History button */}
          {onHistoryClick && (
            <button
              onClick={onHistoryClick}
              className="w-9 h-9 rounded-lg bg-dark-secondary/80 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
          )}
          {/* USDT */}
          <div className="flex items-center gap-1.5 bg-dark-secondary/80 rounded-lg px-2.5 py-1.5">
            <UsdtIcon />
            <span className="text-accent-usdt text-xs sm:text-sm font-medium">USDT</span>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative min-h-[200px] overflow-hidden">
        <MapView
          fromAddress={fromAddress}
          toAddress={toAddress}
          intermediateAddresses={intermediateAddresses}
          waitingTime={selectedTariffData?.estimated_waiting?.message}
          arrivalTime={getArrivalTime()}
        />
      </div>

      {/* Bottom Panel */}
      <div className="bg-dark-tertiary rounded-t-3xl border-t border-primary/10 pb-safe relative z-20">
        {/* Ручка для свайпа */}
        <div className="flex justify-center py-2">
          <div className="w-10 h-1 rounded-full bg-text-muted/30" />
        </div>

        {/* Address inputs */}
        <div className="px-3 sm:px-4 pb-3">
          <div className="flex gap-2 sm:gap-3">
            {/* Route line decoration */}
            <div className="flex flex-col items-center py-2.5 sm:py-3">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-accent-green border-2 border-accent-green/30" />
              <div className="w-0.5 flex-1 bg-gradient-to-b from-accent-green/50 to-accent-red/50 my-1" />
              {intermediateAddresses.map((_, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-2 h-2 rounded-full bg-primary border border-primary/50" />
                  <div className="w-0.5 h-8 bg-primary/30 my-1" />
                </div>
              ))}
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-accent-red border-2 border-accent-red/30" />
            </div>

            {/* Address buttons */}
            <div className="flex-1 space-y-1.5 sm:space-y-2">
              {/* From address */}
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={onFromClick}
                  className="flex-1 text-left px-3 sm:px-4 py-2.5 sm:py-3 bg-dark-secondary rounded-xl border border-primary/10 hover:border-primary/30 active:bg-dark transition-all min-w-0"
                >
                  {fromAddress ? (
                    <div>
                      <span className="text-text-primary truncate block font-medium text-sm sm:text-base">
                        {fromAddress.display_name.split(',')[0]}
                      </span>
                      {getEntranceInfo() && (
                        <span className="text-text-muted text-xs truncate block">{getEntranceInfo()}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-text-muted text-sm sm:text-base">Откуда?</span>
                  )}
                </button>
                
                {/* Кнопка Подъезд */}
                {fromAddress && (
                  <button
                    onClick={() => setShowEntranceModal(true)}
                    className={`px-2.5 sm:px-3 py-2.5 sm:py-3 rounded-xl border transition-all text-xs sm:text-sm font-medium whitespace-nowrap ${
                      entrance
                        ? 'bg-primary/20 border-primary/50 text-primary-light' 
                        : 'bg-dark-secondary border-primary/10 text-text-secondary hover:border-primary/30'
                    }`}
                  >
                    {entrance ? `П. ${entrance}` : 'Подъезд'}
                  </button>
                )}
              </div>

              {/* Промежуточные точки */}
              {intermediateAddresses.map((addr, index) => (
                <div key={index} className="flex gap-1.5 sm:gap-2">
                  <button
                    className="flex-1 text-left px-3 sm:px-4 py-2.5 sm:py-3 bg-dark-secondary rounded-xl border border-primary/20 min-w-0"
                  >
                    <span className="text-primary-light truncate block font-medium text-sm sm:text-base">
                      {addr.display_name.split(',')[0]}
                    </span>
                  </button>
                  <button 
                    onClick={() => onRemoveIntermediate(index)}
                    className="px-3 py-2.5 sm:py-3 rounded-xl bg-dark-secondary border border-primary/10 text-accent-red hover:bg-accent-red/10 transition-all"
                  >
                    <RemoveIcon />
                  </button>
                </div>
              ))}

              {/* To address */}
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  onClick={onToClick}
                  className="flex-1 text-left px-3 sm:px-4 py-2.5 sm:py-3 bg-dark-secondary rounded-xl border border-primary/10 hover:border-primary/30 active:bg-dark transition-all min-w-0"
                >
                  {toAddress ? (
                    <span className="text-text-primary truncate block font-medium text-sm sm:text-base">
                      {toAddress.display_name.split(',')[0]}
                    </span>
                  ) : (
                    <span className="text-text-muted text-sm sm:text-base">Куда?</span>
                  )}
                </button>
                
                {/* Кнопка добавить остановку */}
                {toAddress && intermediateAddresses.length < 3 && (
                  <button 
                    onClick={onAddIntermediateClick}
                    className="px-2.5 sm:px-3 py-2.5 sm:py-3 rounded-xl bg-dark-secondary border border-primary/10 text-text-secondary hover:border-primary/30 hover:text-primary-light transition-all"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tariff selector */}
        {fromAddress && toAddress && (
          <>
            {/* Loading state */}
            {isLoadingTariffs && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-3">
                  <svg className="animate-spin h-5 w-5 text-primary" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-text-secondary text-sm">Загрузка тарифов...</span>
                </div>
              </div>
            )}

            {/* Error state */}
            {tariffsError && !isLoadingTariffs && (
              <div className="mx-3 sm:mx-4 mb-3 bg-accent-red/10 rounded-xl p-3 border border-accent-red/20">
                <p className="text-accent-red text-sm text-center">{tariffsError}</p>
              </div>
            )}

            {/* Tariffs loaded */}
            {!isLoadingTariffs && !tariffsError && tariffs.length > 0 && (
              <>
                {/* Service category tabs */}
                <div className="px-3 sm:px-4">
                  <ServiceTabs 
                    activeTab={serviceCategory} 
                    onTabChange={handleCategoryChange} 
                  />
                </div>

                {/* Tariff cards с автопереключением */}
                <div 
                  ref={tariffScrollRef}
                  className="flex gap-1 overflow-x-auto py-3 px-3 sm:px-4 hide-scrollbar snap-x"
                >
                  {tariffs.map((tariff) => {
                    const category = getCategoryByTariffClass(tariff.class)
                    return (
                      <div 
                        key={tariff.class}
                        data-tariff-class={tariff.class}
                        data-category={category}
                        className="snap-start"
                      >
                        <CompactTariffCard
                          tariff={tariff}
                          isSelected={selectedTariff === tariff.class}
                          onSelect={() => onTariffSelect(tariff.class)}
                        />
                      </div>
                    )
                  })}
                </div>

                {/* USDT Promo Banner */}
                <div className="mx-3 sm:mx-4 mb-3 bg-gradient-to-r from-accent-usdt/10 to-primary/10 rounded-xl p-2.5 sm:p-3 border border-accent-usdt/20 flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-accent-usdt/20 flex items-center justify-center flex-shrink-0">
                    <UsdtIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-xs sm:text-sm font-medium">Оплата в USDT</p>
                    <p className="text-text-muted text-[10px] sm:text-xs truncate">Без комиссии</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted flex-shrink-0">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </div>
              </>
            )}
          </>
        )}

        {/* Order button */}
        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
          <div className="flex gap-1.5 sm:gap-2">
            {/* Payment method indicator */}
            {canOrder && (
              <button className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-dark-secondary border border-primary/10 flex items-center justify-center hover:border-primary/30 transition-all flex-shrink-0">
                <UsdtIcon />
              </button>
            )}
            
            {/* Main order button */}
            <button
              onClick={onOrder}
              disabled={!canOrder || isOrdering}
              className={`flex-1 py-3.5 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all ${
                canOrder && !isOrdering
                  ? 'bg-gradient-primary text-white shadow-purple hover:shadow-purple-lg active:scale-[0.98]'
                  : 'bg-dark-secondary text-text-muted cursor-not-allowed'
              }`}
            >
              {isOrdering ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm sm:text-base">Ищем...</span>
                </span>
              ) : canOrder ? (
                <span className="flex items-center justify-center gap-1.5 sm:gap-2">
                  <span>Заказать</span>
                  <span className="opacity-70">·</span>
                  <span>{selectedTariffData?.price}</span>
                </span>
              ) : (
                'Укажите маршрут'
              )}
            </button>

            {/* Settings button */}
            {canOrder && (
              <button 
                onClick={() => onTariffDetails?.(selectedTariff)}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-dark-secondary border border-primary/10 flex items-center justify-center text-text-secondary hover:border-primary/30 hover:text-primary-light transition-all flex-shrink-0"
              >
                <SettingsIcon />
              </button>
            )}
          </div>

          {/* USDT price */}
          {canOrder && selectedTariffData && (
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <UsdtIcon />
              <p className="text-accent-usdt text-xs sm:text-sm font-medium">
                ≈ {selectedTariffData.price_usdt.toFixed(2)} USDT
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно подъезда */}
      <EntranceModal
        isOpen={showEntranceModal}
        onClose={() => setShowEntranceModal(false)}
        entrance={entrance}
        onEntranceChange={onEntranceChange}
      />
    </div>
  )
}

export default MainScreen
